import React, { useEffect, useState, useContext } from "react";
import { Box, Button, TextField, FormHelperText, Alert, IconButton } from "@mui/material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import * as Yup from 'yup';
import { apiGet, apiPost } from "app/Services/ApiService";
import { apiUrls } from "common/ApiUrls";
import TableAllData from "../CustomTable/TableAllData";
import AximDropdown from "components/Dropdown/AximDropdown";
import AximTooltip from "components/AximTooltip";
import ConfirmDialog from "components/Content/Modal/ConfirmDialog";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";

// Fields intentionally hidden from the grid: internal/audit columns not useful to the end user.
const EXCLUDED_KEYS = new Set([
    "beneficiaryId",
    "dependentId",
    "enrollmentId",
    "insuredId",
    "isDeleted",
    "createDate",
    "updateDate",
    "createUserID",
    "updateUserID",
]);

function toHeader(key) {
    return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, c => c.toUpperCase());
}

function getRelationshipText(row, relationshipPairs) {
    const key = Object.keys(row).find(k => k.toLowerCase() === "relationshipid");
    return key ? (relationshipPairs.find(r => r.value === row[key])?.text || "") : "";
}

const emptyForm = {
    dependentId: "",
    enrollmentId: "",
    primaryPercentage: "",
    secondaryPercentage: "",
};

const percentageFields = {
    primaryPercentage: Yup.number()
        .transform((value, originalValue) => originalValue === "" ? undefined : value)
        .typeError('Primary percentage must be a number')
        .min(0, 'Primary percentage cannot be negative')
        .max(100, 'Primary percentage must not exceed 100')
        .required('Primary percentage is required'),
    secondaryPercentage: Yup.number()
        .transform((value, originalValue) => originalValue === "" ? undefined : value)
        .typeError('Secondary percentage must be a number')
        .min(0, 'Secondary percentage cannot be negative')
        .max(100, 'Secondary percentage must not exceed 100')
        .notRequired(),
};

const addValidationSchema = Yup.object().shape({
    dependentId: Yup.mixed().required('Dependent is required'),
    enrollmentId: Yup.mixed().required('Benefit type is required'),
    ...percentageFields,
});

const editValidationSchema = Yup.object().shape(percentageFields);

const keysChangeCase = (obj, toUpper = true) => Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [(toUpper ? k.charAt(0).toUpperCase() : k.charAt(0).toLowerCase()) + k.slice(1), v])
);

function FundsBeneficiaries({ insuredId, activeBenefits }) {

    const { companyId } = useContext(PrivateRoutesContext);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [dependents, setDependents] = useState([]);
    const [relationshipPairs, setRelationshipPairs] = useState([]);

    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState("add");
    const [editingBeneficiaryID, setEditingBeneficiaryID] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [editDependentName, setEditDependentName] = useState("");
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");

    const [doShowConfirm, setDoShowConfirm] = useState(false);
    const [confirmCloseAction, setConfirmCloseAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [confirmButtonsDisabled, setConfirmButtonsDisabled] = useState(false);
    const [deleteError, setDeleteError] = useState("");

    useEffect(() => {
        apiGet(apiUrls.getRelationships())
            .then(response => {
                const pairs = (response.data || []).map(r => ({ value: r.relationshipId, text: r.relationshipName }));
                setRelationshipPairs(pairs);
            })
            .catch(() => setRelationshipPairs([]));
    }, []);

    useEffect(() => {
        if (!insuredId) return;

        apiGet(apiUrls.getEmployeeDependents(insuredId, companyId))
            .then((response) => {
                setDependents(response.data || []);
            })
            .catch(() => {
                setDependents([]);
            });
    }, [insuredId, companyId]);

    useEffect(() => {

        if (!insuredId) return;

        setIsLoading(true);

        apiGet(apiUrls.getFundsBeneficiaries(insuredId, companyId))
            .then((response) => {
                const raw = response.data || [];

                const dynamicColumns = raw.length > 0
                    ? Object.keys(raw[0])
                        .filter(key => !EXCLUDED_KEYS.has(key))
                        .map(key => key.toLowerCase() === "relationshipid"
                            ? { Header: "Relationship", accessor: row => getRelationshipText(row, relationshipPairs), id: "relationship" }
                            : { Header: toHeader(key), accessor: key })
                    : [];

                setColumns(dynamicColumns);
                setData(raw);
                setIsLoading(false);
            })
            .catch(() => {
                setData([]);
                setColumns([]);
                setIsLoading(false);
            });

    }, [insuredId, companyId, refreshTrigger, relationshipPairs]);

    const handleChange = (name, value) => {
        setFormData(formData => ({ ...formData, [name]: value }));
    };

    const handleAddClick = () => {
        setFormMode("add");
        setEditingBeneficiaryID(null);
        setFormData(emptyForm);
        setEditDependentName("");
        setErrors({});
        setSaveError("");
        setShowForm(true);
    };

    // Looks up a property on obj case-insensitively, trying each candidate name in order.
    const findField = (obj, candidates) => {
        if (!obj) return undefined;
        const keys = Object.keys(obj);
        for (const candidate of candidates) {
            const match = keys.find(k => k.toLowerCase() === candidate.toLowerCase());
            if (match && obj[match] !== undefined && obj[match] !== null && obj[match] !== "") {
                return obj[match];
            }
        }
        return undefined;
    };

    const getDependentName = (row) => {
        const firstName = findField(row, ["firstName", "dependentFirstName"]);
        const middleName = findField(row, ["middleName", "dependentMiddleName"]);
        const lastName = findField(row, ["lastName", "dependentLastName"]);

        if (firstName || lastName) {
            return `${firstName || ""} ${middleName || ""} ${lastName || ""}`.replace(/\s+/g, " ").trim();
        }

        const directName = findField(row, ["dependentName", "fullName", "name"]);
        if (directName) return directName;

        const rowDependentId = findField(row, ["dependentId"]);
        const matchedDependent = dependents.find(d =>
            String(findField(d, ["dependentId", "id"])) === String(rowDependentId)
        );

        if (matchedDependent) {
            const mFirst = findField(matchedDependent, ["firstName"]);
            const mLast = findField(matchedDependent, ["lastName"]);
            return `${mFirst || ""} ${mLast || ""}`.replace(/\s+/g, " ").trim();
        }

        return "";
    };

    const handleEditClick = (row) => {
        setFormMode("edit");
        setEditingBeneficiaryID(row.beneficiaryId);
        const dependentName = getDependentName(row);
        console.debug("[FundsBeneficiaries] edit row keys:", Object.keys(row), row);
        console.debug("[FundsBeneficiaries] resolved dependentName:", dependentName);
        setEditDependentName(dependentName);
        setFormData({
            ...emptyForm,
            primaryPercentage: row.primaryPercentage,
            secondaryPercentage: row.secondaryPercentage ?? "",
        });
        setErrors({});
        setSaveError("");
        setShowForm(true);
    };

    const handleCancel = () => {
        setShowForm(false);
        setFormMode("add");
        setEditingBeneficiaryID(null);
        setEditDependentName("");
        setErrors({});
        setSaveError("");
    };

    const promptDelete = (row) => {
        setDeleteError("");
        setConfirmCloseAction(() => (result) => handleDeleteConfirm(result, row));
        setConfirmTitle("Delete Funds Beneficiary");
        setConfirmText("Are you sure you would like to delete this funds beneficiary record?");
        setDoShowConfirm(true);
    };

    const handleDeleteConfirm = (isConfirmed, row) => {
        if (!isConfirmed) {
            setDoShowConfirm(false);
            return;
        }

        setConfirmButtonsDisabled(true);
        const payload = keysChangeCase({ insuredId:insuredId, beneficiaryId: row.beneficiaryId }, true);

        apiPost(apiUrls.deleteFundsBeneficiary(), payload)
            .then(() => {
                setRefreshTrigger(t => t + 1);
            })
            .catch((error) => {
                setDeleteError(error?.response?.data?.message || error?.response?.data || "Failed to delete funds beneficiary. Please try again.");
            })
            .finally(() => {
                setConfirmButtonsDisabled(false);
                setDoShowConfirm(false);
            });
    };

    const handleConfirmClose = (result) => {
        confirmCloseAction(result);
    };

    const handleSave = async () => {
        setSaveError("");

        const schema = formMode === "edit" ? editValidationSchema : addValidationSchema;

        let validated;
        try {
            validated = await schema.validate(formData, { abortEarly: false });
        }
        catch (validationErrors) {
            const fieldErrors = {};
            for (const err of validationErrors.inner) {
                if (err.path && !fieldErrors[err.path]) {
                    fieldErrors[err.path] = err.message;
                }
            }
            setErrors(fieldErrors);
            return;
        }

        setErrors({});
        setIsSaving(true);

        if (formMode === "edit") {
            const updatedBeneficiary = {
                beneficiaryId: editingBeneficiaryID,
                insuredId,
                primaryPercentage: validated.primaryPercentage,
                secondaryPercentage: validated.secondaryPercentage ?? null,
            };

            apiPost(apiUrls.updateFundsBeneficiary(), keysChangeCase(updatedBeneficiary, true))
                .then(() => {
                    setIsSaving(false);
                    setShowForm(false);
                    setFormMode("add");
                    setEditingBeneficiaryID(null);
                    setRefreshTrigger(t => t + 1);
                })
                .catch((error) => {
                    setIsSaving(false);
                    setSaveError(error?.response?.data?.message || error?.response?.data || "Failed to update funds beneficiary. Please try again.");
                });

            return;
        }

        const selectedDependent = dependents.find(d => (d.dependentId ?? d.dependentID ?? d.id) === validated.dependentId);

        const newBeneficiary = {
            insuredId,
            dependentId: validated.dependentId,
            relationshipId: selectedDependent?.relationshipID,
            enrollmentId: validated.enrollmentId,
            primaryPercentage: validated.primaryPercentage,
            secondaryPercentage: validated.secondaryPercentage ?? null,
            firstName: selectedDependent.firstName,
            middleName: selectedDependent.middleName,
            lastName: selectedDependent.lastName
        };

        apiPost(apiUrls.addFundsBeneficiary(), keysChangeCase(newBeneficiary, true))
            .then(() => {
                setIsSaving(false);
                setShowForm(false);
                setRefreshTrigger(t => t + 1);
            })
            .catch((error) => {
                setIsSaving(false);
                setSaveError(error?.response?.data?.message || error?.response?.data || "Failed to save funds beneficiary. Please try again.");
            });
    };

    const dependentPairs = dependents.map(d => ({
        value: d.dependentId ?? d.dependentID ?? d.id,
        text: `${d.firstName || ""} ${d.lastName || ""}`.replace(/\s+/g, " ").trim(),
    }));

    const benefitTypePairs = (activeBenefits || []).map(b => ({
        value: b.enrollmentID,
        text: b.benefitType,
    }));

    const actionColumn = {
        Header: "Action",
        id: "actions",
        Cell: ({ row }) => (
            <>
                <AximTooltip title="Edit">
                    <IconButton color="primary" onClick={() => handleEditClick(row.original)}>
                        <EditOutlinedIcon />
                    </IconButton>
                </AximTooltip>
                <AximTooltip title="Delete">
                    <IconButton color="primary" onClick={() => promptDelete(row.original)}>
                        <DeleteOutlinedIcon />
                    </IconButton>
                </AximTooltip>
            </>
        ),
        disableFilters: true,
    };

    const tableColumns = columns.length > 0 ? [...columns, actionColumn] : columns;

    return (
        <Box className="container-fluid">

            <Box className="d-flex justify-content-end mb-2">
                {!showForm && activeBenefits && activeBenefits.length > 0 &&
                    <Button variant="contained" onClick={handleAddClick}>Add Funds Beneficiary</Button>
                }
            </Box>

            {deleteError &&
                <Box className="mb-2">
                    <Alert severity="error">{deleteError}</Alert>
                </Box>
            }

            {showForm &&
                <Box className="row border rounded p-3 mb-3">
                    {formMode === "edit" ? (
                        <Box className="col-12 col-md-4 mb-2">
                            <TextField
                                fullWidth
                                variant="standard"
                                label="Name"
                                value={editDependentName}
                                InputProps={{ readOnly: true }}
                                disabled
                            />
                        </Box>
                    ) : (
                        <>
                            <Box className="col-12 col-md-4 mb-2">
                                <AximDropdown
                                    id="beneficiary-dependentId"
                                    label="Dependent"
                                    value={formData.dependentId}
                                    handleChange={e => handleChange('dependentId', e.target.value)}
                                    pairs={dependentPairs}
                                />
                                {errors.dependentId &&
                                    <FormHelperText error>{errors.dependentId}</FormHelperText>
                                }
                            </Box>
                            <Box className="col-12 col-md-4 mb-2">
                                <AximDropdown
                                    id="beneficiary-enrollmentId"
                                    label="Benefit Type"
                                    value={formData.enrollmentId}
                                    handleChange={e => handleChange('enrollmentId', e.target.value)}
                                    pairs={benefitTypePairs}
                                />
                                {errors.enrollmentId &&
                                    <FormHelperText error>{errors.enrollmentId}</FormHelperText>
                                }
                            </Box>
                        </>
                    )}
                    <Box className="col-12 col-md-4 mb-2">
                        <TextField
                            fullWidth
                            variant="standard"
                            type="number"
                            label="Primary Percentage"
                            value={formData.primaryPercentage}
                            onChange={e => handleChange('primaryPercentage', e.target.value)}
                            error={!!errors.primaryPercentage}
                            helperText={errors.primaryPercentage}
                        />
                    </Box>
                    <Box className="col-12 col-md-4 mb-2">
                        <TextField
                            fullWidth
                            variant="standard"
                            type="number"
                            label="Secondary Percentage"
                            value={formData.secondaryPercentage}
                            onChange={e => handleChange('secondaryPercentage', e.target.value)}
                            error={!!errors.secondaryPercentage}
                            helperText={errors.secondaryPercentage}
                        />
                    </Box>

                    {saveError &&
                        <Box className="col-12 mb-2">
                            <Alert severity="error">{saveError}</Alert>
                        </Box>
                    }

                    <Box className="col-12 d-flex justify-content-end">
                        <Button onClick={handleCancel} disabled={isSaving} className="me-2">Cancel</Button>
                        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </Box>
                </Box>
            }

            <TableAllData
                key={`funds-beneficiaries-${insuredId}`}
                columns={tableColumns}
                data={data}
                isLoading={isLoading}
            />

            <ConfirmDialog
                doShowConfirm={doShowConfirm}
                handleClose={handleConfirmClose}
                confirmTitle={confirmTitle}
                confirmText={confirmText}
                buttonsDisabled={confirmButtonsDisabled}
            />

        </Box>
    );
}

export default FundsBeneficiaries;
